// Page side of the content-script bridge to the extension service worker (see extension/sw.js).

export interface BridgeResponse {
  ok: boolean;
  error?: string;
  [k: string]: unknown;
}

let ready = false;

let nextRid = 1;
const pending = new Map<number, (resp: BridgeResponse) => void>();

window.addEventListener('message', (ev: MessageEvent) => {
  if (ev.source !== window) return;
  const d = ev.data;
  if (!d || typeof d !== 'object') return;
  if (d.__kg === 'BRIDGE_READY') {
    ready = true; // fast path only; waitForBridge never depends on this arriving
    return;
  }
  if (d.__kg === 'RES' && typeof d.rid === 'number') {
    const cb = pending.get(d.rid);
    if (cb) {
      pending.delete(d.rid);
      cb(d.resp || { ok: false, error: 'empty response' });
    }
  }
});

export function bridgeIsReady(): boolean {
  return ready;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Actively probes rather than waiting on BRIDGE_READY, which posts before this module runs and would be missed.
export async function waitForBridge(timeoutMs = 2500): Promise<boolean> {
  if (ready) return true;
  const deadline = performance.now() + timeoutMs;
  for (;;) {
    const remaining = deadline - performance.now();
    if (remaining <= 0) return false;
    const r = await callBridge({ type: 'PING' }, Math.min(600, remaining));
    if (r.ok) {
      ready = true;
      return true;
    }
    if (performance.now() >= deadline) return false;
    await sleep(120);
  }
}

export function callBridge(payload: Record<string, unknown>, timeoutMs = 20_000): Promise<BridgeResponse> {
  return new Promise((resolve) => {
    const rid = nextRid++;
    const timer = setTimeout(() => {
      if (pending.delete(rid)) {
        resolve({ ok: false, error: 'extension did not respond (is it loaded and enabled on this site?)' });
      }
    }, timeoutMs);
    pending.set(rid, (resp) => {
      clearTimeout(timer);
      resolve(resp);
    });
    window.postMessage({ __kg: 'REQ', rid, payload }, window.location.origin);
  });
}

export interface ExtensionInfo {
  id: string;
  version: string;
  /** Whether the worker will accept anything else from this origin. */
  authorized: boolean;
}

export async function pingExtension(): Promise<ExtensionInfo | null> {
  const r = await callBridge({ type: 'PING' }, 4000);
  return r.ok
    ? { id: String(r.id), version: String(r.version), authorized: r.authorized !== false }
    : null;
}
